import i18n from '@/i18n'
import moment from 'moment'
import store from '@/store'
import { companyOffsetToLocalDate } from '@/utils/DateFormatter'
import fileNameConfig from '@/utils/FileNames.json'

export function fileNameSymbolsValid (fileName) {
  const regex = new RegExp(/[/\\:*?"<>|#%]/, 'g')
  if (regex.test(fileName)) {
    return i18n.t('fileNameCannotIncludeTheseSymbols')
  } else {
    return true
  }
}

export function fileNameSafeString (string) {
  return string.replaceAll(/[<>:"/\\|?*.\s]+/g, '')
}

export function appendDateToFileName (fileName) {
  return fileName.concat(`_${moment().format('YYYYMMDD')}`)
}

const BYTES_PER_MB = 1000 * 1000
const MAX_ATTACHMENT_MB = 5
export const MAX_ATTACHMENT_FILES = 10
export const ATTACHMENT_MIMES = new Set([
  'application/pdf',
  'application/msword',
  'text/plain',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'text/csv',
  'image/png',
  'image/jpeg',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.ms-word'
])

export function validateAttachment (file, isAllowedType) {
  if (file.size > MAX_ATTACHMENT_MB * BYTES_PER_MB) return i18n.t('fileTooLargeSnack', { limit: `${MAX_ATTACHMENT_MB}MB` })
  const symbols = fileNameSymbolsValid(file.name)
  if (symbols !== true) return symbols
  if (!isAllowedType(file)) return i18n.t('fileMimeNotAllowedSnack')
  return null
}

export function uniqueFileName (name, takenNames) {
  if (!takenNames.includes(name)) return name
  const dot = name.lastIndexOf('.')
  const stem = dot > 0 ? name.slice(0, dot) : name
  const ext = dot > 0 ? name.slice(dot) : ''
  let n = 2
  while (takenNames.includes(`${stem} (${n})${ext}`)) n++
  return `${stem} (${n})${ext}`
}

const MAX_COMPANY_NAME_SEGMENT_LENGTH = 10

export function fileNameSegment (value, maxLength) {
  const segment = String(value ?? '')
    .trim()
    .replace(/\s+/g, '-')
    .replace(/[^A-Za-z0-9-]/g, '')
    .replace(/-{2,}/g, '-')
    .replace(/^-+/, '')
    .slice(0, maxLength)
    .replace(/-+$/, '')
  return segment || 'Unknown'
}

// Company name should be Short Name, but as some do not have one, we will place the normal
// company name with a max length of 10
function companyFileNameSegment () {
  const companyInfo = store.getters['user/companyInfo']
  return companyInfo?.shortName
    ? fileNameSegment(companyInfo.shortName)
    : fileNameSegment(companyInfo?.name, MAX_COMPANY_NAME_SEGMENT_LENGTH)
}

function companyDate (date, format) {
  const formatted = companyOffsetToLocalDate(date, format)
  if (formatted === 'N/A') throw new Error(`Invalid date for file name: ${date}`)
  return formatted
}

function dateRange ({ start, end, exclusiveEnd = false }) {
  const startDate = companyDate(start, 'YYYYMMDD')
  const endDate = companyDate(exclusiveEnd ? moment.utc(end).subtract(1, 'day').format() : end, 'YYYYMMDD')
  return startDate === endDate ? startDate : `${startDate}-${endDate}`
}

const contentDateFormatters = {
  date: value => companyDate(value, 'YYYYMMDD'),
  range: dateRange,
  year: value => (/^\d{4}$/.test(String(value)) ? String(value) : companyDate(value, 'YYYY'))
}

function code (codeType, value) {
  const codes = fileNameConfig.codes[codeType]
  if (Object.values(codes).includes(value)) return value
  const mapped = codes[value?.label ?? value]
  if (mapped === undefined) throw new Error(`No ${codeType} file name code for ${value?.label ?? value}`)
  return mapped
}

const isEmpty = value => value === undefined || value === null || value === ''

function resolvePart (part, values) {
  const token = /^\{(\w+)(\?)?(?::(\w+))?\}$/.exec(part)
  if (!token) {
    if (/[{}]/.test(part)) throw new Error(`Malformed file name token: ${part}`)
    return part
  }

  const [, key, optional, type] = token
  if (key === 'company') return companyFileNameSegment()
  if (key === 'generated') return moment().format('YYYYMMDD')
  if (key === 'generatedWithTime') return moment().format('YYYYMMDD[T]HHmmss')

  const value = values[key]
  if (optional && isEmpty(value)) return undefined
  if (!type) return fileNameSegment(value)
  if (value === undefined || value === null) throw new Error(`Missing file name value: ${key}`)
  if (contentDateFormatters[type]) return contentDateFormatters[type](value)
  if (fileNameConfig.codes[type]) return code(type, value)
  throw new Error(`Unknown file name value type: ${type}`)
}

function toName (name, template, values = {}) {
  const extensionStart = template.lastIndexOf('.')
  const extension = template.slice(extensionStart + 1)
  if (extensionStart < 1 || !/^[a-z0-9]+$/.test(extension)) throw new Error(`File name ${name} must end with a lowercase extension`)

  const parts = template.slice(0, extensionStart).split('_')
  if (!['{generated}', '{generatedWithTime}'].includes(parts[parts.length - 1])) throw new Error(`File name ${name} must end with {generated} or {generatedWithTime}`)

  const segments = parts
    .map(part => resolvePart(part, values))
    .filter(segment => segment !== undefined)

  return `${segments.join('_')}.${extension}`
}

export const FileNames = Object.freeze(Object.fromEntries(
  Object.entries(fileNameConfig.names).map(([name, template]) => [name, values => toName(name, template, values)])
))
